"use client";

import React, { useState } from "react";
import { ShieldCheck, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query = new WP_Query({
  post_type: "project",
  per_page: 100
});
const CATEGORIES = ["All Projects", "Commercial", "Residential", "Roofing", "Temporary Roofs", "New Build", "Industrial", "Heritage", "Specialist Access"];
function ProjectsGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  const [selectedCategory, setSelectedCategory] = useState("All Projects");
  if (loading) {
    return <div className="space-y-8">
        {/* Filter skeleton */}
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((cat, idx) => <div key={idx} className="h-9 w-24 bg-muted/60 rounded-md animate-pulse" />)}
        </div>
        {/* Grid skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {[...Array(6)].map((_, i) => <div key={i} className="h-96 rounded-lg bg-muted/40 animate-pulse border border-border" />)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="p-8 text-center bg-card border border-border rounded-lg space-y-4">
        <p className="text-sm text-muted-foreground font-default">
          Unable to load projects at this time.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </div>;
  }
  const filteredPosts = posts.filter(post => {
    if (selectedCategory === "All Projects") return true;
    const acf = (post as any).acf || post.customFields || {};
    const category = String(acf.category || "").toLowerCase();
    return category.includes(selectedCategory.toLowerCase());
  });
  return <div className="space-y-8">
      {/* Category Filter Bar */}
      <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-border/60">
        {CATEGORIES.map(category => {
        const isActive = selectedCategory === category;
        return <Button key={category} variant={isActive ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory(category)} className={isActive ? "bg-primary text-primary-foreground font-semibold shadow-xs" : "border-border text-foreground hover:bg-accent font-medium text-xs sm:text-sm"}>
              {category}
            </Button>;
      })}
      </div>

      {/* Projects Grid */}
      {filteredPosts.length === 0 ? <div className="p-12 text-center bg-card border border-border rounded-lg space-y-2">
          <p className="text-base font-semibold text-foreground font-default">
            No projects found
          </p>
          <p className="text-sm text-muted-foreground font-default">
            There are currently no projects matching the "{selectedCategory}" category.
          </p>
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {filteredPosts.map((post, i) => {
        const acf = (post as any).acf || post.customFields || {};
        const projectTitle = acf.project_title;
        const category = acf.category;
        const summary = acf.summary;
        const imageRaw = acf.image || post.featuredImage;
        const image = {
          src: imageRaw || "",
          srcset: "",
          sizes: ""
        };
        return <div key={post.id} data-index={i} className="flex">
                <Card className="flex flex-col w-full overflow-hidden border border-border bg-card rounded-lg">
                  {/* Image Container */}
                  <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
                    {image.src ? <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={projectTitle || "Scaffolding Project"} loading="lazy" decoding="async" className="h-full w-full object-cover" /> : <div className="h-full w-full bg-muted flex items-center justify-center text-xs text-muted-foreground">
                        No project image
                      </div>}
                    {category && <div className="absolute top-3 left-3 flex flex-wrap gap-2">
                        <Badge variant="secondary" className="bg-secondary/90 backdrop-blur-xs text-secondary-foreground text-xs font-medium border border-border/40 shadow-xs">
                          {category}
                        </Badge>
                      </div>}
                  </div>

                  {/* Content */}
                  <CardContent className="p-6 space-y-3">
                    {projectTitle && <h3 className="text-xl font-bold text-foreground font-default tracking-tight">
                        {projectTitle}
                      </h3>}
                    {summary && <p className="text-sm text-muted-foreground font-default leading-relaxed line-clamp-3">
                        {summary}
                      </p>}
                  </CardContent>
                </Card>
              </div>;
      })}
        </div>}
    </div>;
}
export default function Projects() {
  return <section id="featured-projects" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border transition-colors duration-300">
      <div className="container mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-12 border-b border-border/80 mb-12">
          <div className="max-w-2xl space-y-3">
            <div className="inline-flex items-center gap-2">
              <span className="h-0.5 w-6 bg-primary" aria-hidden="true" />
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-default">
                Proven Track Record
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-default">
              Recent site work across the South East
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
              Every scaffold is built to strict safety standards, inspected before handover, and supported by our experienced team across Berkshire, Surrey, Hampshire, Buckinghamshire, and London.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 px-3.5 py-2 rounded-md bg-card border border-border text-xs font-semibold text-foreground shadow-xs">
              <ShieldCheck className="size-4 text-primary shrink-0" />
              <span>NASC · CHAS</span>
            </div>
          </div>
        </div>

        {/* Dynamic Projects via PostsProvider */}
        <PostsProvider wp_query={{
        post_type: "project",
        per_page: 100,
        status: ["publish"]
      }}>
          <ProjectsGridConsumer />
        </PostsProvider>

        {/* Section Bottom Banner & CTA Strip */}
        <div className="mt-14 p-6 sm:p-8 rounded-lg border border-border bg-card shadow-xs flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1.5 text-center md:text-left">
            <h2 className="text-lg sm:text-xl font-bold text-foreground font-default">
              Need a tailored scaffolding setup for your next project?
            </h2>
            <p className="text-sm text-muted-foreground font-default">
              From high-street commercial facades to domestic roofing, we supply complete design, erection, and safety sign-off.
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6 shadow-xs">
              <Link to="/ContactRequestQuotePage">
                Request a quote
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-accent font-medium px-5">
              <Link to="/CommercialScaffoldingPage">
                View commercial specs
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}